import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Search, UserCircle, LogOut, Settings, LayoutGrid, Users, MessageCircle, MapPin, Calendar, PartyPopper, ChevronDown, ChevronRight, ChevronUp, Folder, GripVertical, Home, X, BarChart3, Briefcase, HeadphonesIcon, Building2 } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { getUnreadCount } from '../../services/messagesService';
import { getProfilePhoto, getFileUrl } from '../../services/filesService';
import { getPeopleModules, addModuleToGroup, removeModuleFromGroup, reorderGroupModules, NAV_TO_MODULE_MAP } from '../../services/modulesService';
import { getPeoplePreferences, savePeoplePreferences } from '../../services/apiService';
import { getCollapsedSidebarGroups, saveCollapsedSidebarGroups } from '../../services/preferencesService';
import { MODULE_ID_MAP } from '../../hooks/useModules';

/**
 * Menu Component
 * Displays user info, navigation menu, pinned modules/groups, and logout button
 *
 * @param {Object} props
 * @param {string} props.currentPage - Current active page
 * @param {Function} props.onNavigate - Navigation handler
 * @param {Function} props.onLogout - Logout handler
 */
const Menu = ({ currentPage, onNavigate, onLogout }) => {
    const { t, language } = useTranslation();
    const { currentTheme } = useTheme();

    // Helper to normalize object keys to lowercase (API returns uppercase)
    const normalizeKeys = (obj) => {
        if (!obj || typeof obj !== 'object') return obj;
        if (Array.isArray(obj)) return obj.map(normalizeKeys);
        return Object.keys(obj).reduce((acc, key) => {
            const lowerKey = key.toLowerCase();
            acc[lowerKey] = normalizeKeys(obj[key]);
            return acc;
        }, {});
    };

    // Unread messages count (for badge on Modules icon)
    const [unreadMessages, setUnreadMessages] = useState(0);

    // Profile photo URL
    const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);

    // Unified pinned items (modules and groups)
    // Format: [{ type: 'module', id: 'contacts' }, { type: 'group', id: 123 }]
    const [pinnedItems, setPinnedItems] = useState(() => {
        // Start with localStorage as cache
        const stored = localStorage.getItem('cabazaPinnedItems');
        if (stored) {
            return JSON.parse(stored);
        }
        // Migrate from old format
        const oldModules = localStorage.getItem('cabazaPinnedModules');
        const oldGroups = localStorage.getItem('cabazaPinnedGroups');
        const items = [];
        if (oldModules) {
            JSON.parse(oldModules).forEach(id => items.push({ type: 'module', id }));
        }
        if (oldGroups) {
            JSON.parse(oldGroups).forEach(id => items.push({ type: 'group', id }));
        }
        if (items.length > 0) {
            localStorage.setItem('cabazaPinnedItems', JSON.stringify(items));
        }
        return items;
    });

    // Track if we've loaded from API
    const [preferencesLoaded, setPreferencesLoaded] = useState(false);
    // Ref to debounce API saves
    const saveTimeoutRef = useRef(null);

    // Legacy getters for compatibility
    const pinnedModules = pinnedItems.filter(i => i.type === 'module').map(i => i.id);
    const pinnedGroups = pinnedItems.filter(i => i.type === 'group').map(i => i.id);

    // Groups data from API (cached in localStorage)
    const [groupsData, setGroupsData] = useState(() => {
        const stored = localStorage.getItem('cabazaGroupsData');
        return stored ? JSON.parse(stored) : [];
    });

    // Collapsed groups state in sidebar (persisted in localStorage)
    const [collapsedSidebarGroups, setCollapsedSidebarGroups] = useState(() => {
        const stored = localStorage.getItem('cabazaCollapsedSidebarGroups');
        return stored ? JSON.parse(stored) : {};
    });

    // Drag over state for drop zone
    const [isDragOver, setIsDragOver] = useState(false);

    // Drag state for reordering pinned items
    const [draggedPinnedItem, setDraggedPinnedItem] = useState(null);
    const [dragOverPinnedIndex, setDragOverPinnedIndex] = useState(null);
    const [dragOverGroupId, setDragOverGroupId] = useState(null);

    // Drag state for reordering modules within a group
    const [draggedGroupModule, setDraggedGroupModule] = useState(null); // { groupId, moduleIndex, module }
    const [dragOverGroupModuleItemId, setDragOverGroupModuleItemId] = useState(null);

    // Track if there's an external module drag (from Modules page)
    const [isExternalModuleDrag, setIsExternalModuleDrag] = useState(false);

    // Group popup state - which group is open in the popup overlay
    const [openGroupPopup, setOpenGroupPopup] = useState(null);

    // Module visibility from localStorage
    const [enabledModules, setEnabledModules] = useState(() => {
        const defaultModules = { contacts: true, map: true, agenda: true, messages: true, events: true };
        const stored = localStorage.getItem('cabazaModules');
        return stored ? { ...defaultModules, ...JSON.parse(stored) } : defaultModules;
    });

    const userId = parseInt(localStorage.getItem('userId'), 10);

    // Module definitions for pinned display
    const moduleDefinitions = {
        contacts: { icon: Users, label: t.menu?.contacts || 'Contacts', code: 'CONTACTS' },
        messages: { icon: MessageCircle, label: t.menu?.messages || 'Messages', code: 'CONVERSATIONS' },
        map: { icon: MapPin, label: t.menu?.map || 'Carte', code: 'MAP' },
        calendar: { icon: Calendar, label: t.menu?.calendar || 'Agenda', code: 'AGENDA' },
        events: { icon: PartyPopper, label: t.menu?.events || 'Événements', code: 'EVENTS' },
        places: { icon: MapPin, label: t.menu?.places || 'Endroits', code: 'PLACES' },
    };

    // Load groups data (cached in localStorage)
    const loadGroups = async () => {
        if (userId) {
            try {
                const data = await getPeopleModules(userId, language);
                const normalizedData = normalizeKeys(data);

                // Always update state from API
                setGroupsData(normalizedData);

                // Only update localStorage if different (to avoid unnecessary writes)
                const storedData = localStorage.getItem('cabazaGroupsData');
                const newDataStr = JSON.stringify(normalizedData);
                if (storedData !== newDataStr) {
                    localStorage.setItem('cabazaGroupsData', newDataStr);
                }
            } catch (error) {
                console.error('Error loading groups:', error);
            }
        }
    };

    useEffect(() => {
        loadGroups();
    }, [userId, language]);

    // Load preferences from API on mount
    useEffect(() => {
        const loadPreferences = async () => {
            if (userId) {
                try {
                    const prefs = await getPeoplePreferences(userId);
                    const pinnedItemsFromApi = prefs.pinnedItems || prefs.pinneditems || [];

                    // Always update state from API if we have data
                    if (pinnedItemsFromApi.length > 0) {
                        const apiDataStr = JSON.stringify(pinnedItemsFromApi);
                        const storedData = localStorage.getItem('cabazaPinnedItems');

                        // Update state
                        setPinnedItems(pinnedItemsFromApi);

                        // Only update localStorage if different (to avoid unnecessary writes)
                        if (storedData !== apiDataStr) {
                            localStorage.setItem('cabazaPinnedItems', apiDataStr);
                        }
                    }

                    // Load collapsed sidebar groups from API
                    const collapsedFromApi = prefs.collapsedSidebarGroups || prefs.collapsedsidebargroups || null;
                    const storedCollapsed = localStorage.getItem('cabazaCollapsedSidebarGroups');
                    const localCollapsed = storedCollapsed ? JSON.parse(storedCollapsed) : {};

                    if (collapsedFromApi && Object.keys(collapsedFromApi).length > 0) {
                        // DB has collapsed groups - load it to localStorage
                        const apiCollapsedStr = JSON.stringify(collapsedFromApi);
                        if (storedCollapsed !== apiCollapsedStr) {
                            console.log('Syncing collapsed groups from API');
                            setCollapsedSidebarGroups(collapsedFromApi);
                            localStorage.setItem('cabazaCollapsedSidebarGroups', apiCollapsedStr);
                        }
                    } else if (Object.keys(localCollapsed).length > 0) {
                        // DB is empty but localStorage has collapsed groups - push to API
                        console.log('Pushing collapsed groups from localStorage to API');
                        saveCollapsedSidebarGroups(localCollapsed);
                    }

                    setPreferencesLoaded(true);
                } catch (error) {
                    console.error('Error loading preferences:', error);
                    setPreferencesLoaded(true); // Still mark as loaded so we can save
                }
            } else {
                setPreferencesLoaded(true);
            }
        };
        loadPreferences();
    }, [userId]);

    // Save preferences to API when pinned items change (debounced)
    useEffect(() => {
        if (!preferencesLoaded || !userId) return;

        // Clear any pending save
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        // Debounce API save (500ms)
        saveTimeoutRef.current = setTimeout(async () => {
            try {
                await savePeoplePreferences(userId, { pinnedItems });
            } catch (error) {
                console.error('Error saving preferences:', error);
            }
        }, 500);

        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [pinnedItems, preferencesLoaded, userId]);

    // Listen for modules/groups changes from Preferences/Modules
    useEffect(() => {
        const handleModulesGroupsChange = () => {
            // Read from localStorage for immediate update (optimistic)
            // Don't call loadGroups() immediately - it would overwrite with old API data
            const stored = localStorage.getItem('cabazaGroupsData');
            if (stored) {
                const normalizedData = normalizeKeys(JSON.parse(stored));
                setGroupsData(normalizedData);
            }
        };
        window.addEventListener('modulesGroupsChanged', handleModulesGroupsChange);
        // Also listen for pin toggle events (separate from structural changes)
        window.addEventListener('modulePinnedToggled', handleModulesGroupsChange);
        return () => {
            window.removeEventListener('modulesGroupsChanged', handleModulesGroupsChange);
            window.removeEventListener('modulePinnedToggled', handleModulesGroupsChange);
        };
    }, [userId, language]);

    // Listen for pinned items changes
    useEffect(() => {
        const handlePinnedItemsChange = () => {
            const stored = localStorage.getItem('cabazaPinnedItems');
            setPinnedItems(stored ? JSON.parse(stored) : []);
            loadGroups();
        };
        window.addEventListener('pinnedItemsChanged', handlePinnedItemsChange);
        // Also listen for legacy events
        window.addEventListener('pinnedModulesChanged', handlePinnedItemsChange);
        window.addEventListener('pinnedGroupsChanged', handlePinnedItemsChange);
        return () => {
            window.removeEventListener('pinnedItemsChanged', handlePinnedItemsChange);
            window.removeEventListener('pinnedModulesChanged', handlePinnedItemsChange);
            window.removeEventListener('pinnedGroupsChanged', handlePinnedItemsChange);
        };
    }, [userId, language]);

    // Listen for module visibility changes
    useEffect(() => {
        const handleModulesChange = () => {
            const defaultModules = { contacts: true, map: true, agenda: true, messages: true, events: true };
            const stored = localStorage.getItem('cabazaModules');
            setEnabledModules(stored ? { ...defaultModules, ...JSON.parse(stored) } : defaultModules);
        };
        window.addEventListener('modulesChanged', handleModulesChange);
        return () => window.removeEventListener('modulesChanged', handleModulesChange);
    }, []);

    // Listen for global dragend to reset external drag state
    useEffect(() => {
        const handleGlobalDragEnd = () => {
            setIsExternalModuleDrag(false);
            setDragOverGroupId(null);
        };
        document.addEventListener('dragend', handleGlobalDragEnd);
        return () => document.removeEventListener('dragend', handleGlobalDragEnd);
    }, []);

    // Handle drag over
    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        setIsDragOver(true);
    };

    // Handle drag leave
    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    // Handle drop
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);

        const isGroup = e.dataTransfer.getData('isGroup') === 'true';
        const groupId = e.dataTransfer.getData('groupId');
        const moduleCode = e.dataTransfer.getData('moduleCode');

        if (isGroup && groupId) {
            // Pin group
            const numGroupId = parseInt(groupId, 10);
            if (!pinnedItems.some(i => i.type === 'group' && i.id === numGroupId)) {
                const newPinned = [...pinnedItems, { type: 'group', id: numGroupId }];
                setPinnedItems(newPinned);
                localStorage.setItem('cabazaPinnedItems', JSON.stringify(newPinned));
                window.dispatchEvent(new CustomEvent('pinnedItemsChanged'));
            }
        } else if (moduleCode) {
            // Pin module (convert code to nav id)
            const navMap = {
                'CONTACTS': 'contacts',
                'CONVERSATIONS': 'messages',
                'MAP': 'map',
                'AGENDA': 'calendar',
                'EVENTS': 'events',
                'PLACES': 'places'
            };
            const moduleId = navMap[moduleCode] || moduleCode.toLowerCase();
            if (!pinnedItems.some(i => i.type === 'module' && i.id === moduleId)) {
                const newPinned = [...pinnedItems, { type: 'module', id: moduleId }];
                setPinnedItems(newPinned);
                localStorage.setItem('cabazaPinnedItems', JSON.stringify(newPinned));
                window.dispatchEvent(new CustomEvent('pinnedItemsChanged'));
            }
        }
    };

    // Toggle sidebar group collapse (persisted in localStorage and API)
    const toggleSidebarGroup = (groupId) => {
        setCollapsedSidebarGroups(prev => {
            const newState = {
                ...prev,
                [groupId]: !prev[groupId]
            };
            localStorage.setItem('cabazaCollapsedSidebarGroups', JSON.stringify(newState));
            // Sync to API (debounced)
            saveCollapsedSidebarGroups(newState);
            return newState;
        });
    };

    // Handle pinned item drag start (unified for modules and groups)
    const handlePinnedItemDragStart = (e, item, index) => {
        setDraggedPinnedItem({ item, index });
        e.dataTransfer.effectAllowed = 'move';
    };

    // Handle pinned item drag over
    const handlePinnedItemDragOver = (e, index) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverPinnedIndex(index);
        setDragOverGroupId(null);
    };

    // Handle drag over a group (to add module to group)
    const handleGroupDragOver = (e, groupId) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverGroupId(groupId);
        setDragOverPinnedIndex(null);
        // Detect external drag (from Modules page) by checking if moduleCode is in dataTransfer types
        // Note: dataTransfer.types stores keys in lowercase
        const hasModuleData = e.dataTransfer.types.some(t => t.toLowerCase() === 'modulecode');
        if (!draggedGroupModule && !draggedPinnedItem && hasModuleData) {
            setIsExternalModuleDrag(true);
        }
    };

    // Handle drag leave from a group
    const handleGroupDragLeave = (e) => {
        // Only reset if we're leaving the group container, not entering a child
        const relatedTarget = e.relatedTarget;
        const currentTarget = e.currentTarget;
        if (!currentTarget.contains(relatedTarget)) {
            setDragOverGroupId(null);
        }
    };

    // Handle pinned item drag end
    const handlePinnedItemDragEnd = () => {
        setDraggedPinnedItem(null);
        setDragOverPinnedIndex(null);
        setDragOverGroupId(null);
        setIsExternalModuleDrag(false);
    };

    // Handle drop on pinned item (reorder)
    const handlePinnedItemDrop = (e, targetIndex) => {
        e.preventDefault();
        e.stopPropagation();

        if (draggedPinnedItem && draggedPinnedItem.index !== targetIndex) {
            const newPinned = [...pinnedItems];
            const [removed] = newPinned.splice(draggedPinnedItem.index, 1);
            newPinned.splice(targetIndex, 0, removed);
            setPinnedItems(newPinned);
            localStorage.setItem('cabazaPinnedItems', JSON.stringify(newPinned));
            window.dispatchEvent(new CustomEvent('pinnedItemsChanged'));
        }

        handlePinnedItemDragEnd();
    };

    // Handle drop on group (add module to group or move between groups)
    const handleDropOnGroup = async (e, targetGroupId) => {
        e.preventDefault();
        e.stopPropagation();

        // Check if it's a module being dragged from within a group (group module drag)
        if (draggedGroupModule) {
            const sourceGroupId = draggedGroupModule.groupId;
            const moduleCode = draggedGroupModule.module.code;
            const numericModuleId = MODULE_ID_MAP[moduleCode];

            // Don't do anything if dropping on same group
            if (sourceGroupId == targetGroupId) {
                handleGroupModuleDragEnd();
                handlePinnedItemDragEnd();
                return;
            }

            if (numericModuleId) {
                try {
                    // Remove from source group
                    await removeModuleFromGroup(sourceGroupId, numericModuleId);
                    // Add to target group
                    await addModuleToGroup(targetGroupId, numericModuleId);
                    // Reload groups data and notify other components
                    await loadGroups();
                    window.dispatchEvent(new CustomEvent('modulesGroupsChanged'));
                } catch (error) {
                    console.error('Error moving module between groups:', error);
                }
            }

            handleGroupModuleDragEnd();
            handlePinnedItemDragEnd();
            return;
        }

        // Handle pinned module being dropped on a group
        if (draggedPinnedItem && draggedPinnedItem.item.type === 'module') {
            const moduleId = draggedPinnedItem.item.id;
            const moduleCode = NAV_TO_MODULE_MAP[moduleId];

            if (moduleCode) {
                const numericModuleId = MODULE_ID_MAP[moduleCode];

                if (numericModuleId) {
                    const result = await addModuleToGroup(targetGroupId, numericModuleId);
                    const success = result.success ?? result.SUCCESS;
                    if (success) {
                        // Remove from pinned items
                        const newPinned = pinnedItems.filter(i => !(i.type === 'module' && i.id === moduleId));
                        setPinnedItems(newPinned);
                        localStorage.setItem('cabazaPinnedItems', JSON.stringify(newPinned));
                        window.dispatchEvent(new CustomEvent('pinnedItemsChanged'));

                        // Reload groups data
                        loadGroups();
                    }
                }
            }
            handlePinnedItemDragEnd();
            return;
        }

        // Handle module dragged from Modules page (via dataTransfer)
        const moduleCode = e.dataTransfer.getData('moduleCode');
        const sourceGroupId = e.dataTransfer.getData('sourceGroupId') || e.dataTransfer.getData('groupId');

        if (moduleCode) {
            const numericModuleId = MODULE_ID_MAP[moduleCode];

            if (numericModuleId) {
                // Don't do anything if dropping on same group
                if (sourceGroupId && parseInt(sourceGroupId) == targetGroupId) {
                    handlePinnedItemDragEnd();
                    return;
                }

                try {
                    // If coming from another group, remove from source first
                    if (sourceGroupId) {
                        await removeModuleFromGroup(parseInt(sourceGroupId), numericModuleId);
                    }

                    // Add to target group
                    await addModuleToGroup(targetGroupId, numericModuleId);

                    // Reload groups data and notify other components
                    await loadGroups();
                    window.dispatchEvent(new CustomEvent('modulesGroupsChanged'));
                } catch (error) {
                    console.error('Error moving module from Modules page:', error);
                }
            }
        }

        handlePinnedItemDragEnd();
    };

    // Handle module drag start within a group
    const handleGroupModuleDragStart = (e, groupId, moduleIndex, module) => {
        e.stopPropagation();
        setDraggedGroupModule({ groupId, moduleIndex, module });
        // Also set dataTransfer for cross-component drag (sidebar to Modules page)
        e.dataTransfer.setData('moduleCode', module.code);
        e.dataTransfer.setData('sourceGroupId', groupId.toString());
        e.dataTransfer.setData('moduleId', (module.moduleid || module.id).toString());
        e.dataTransfer.effectAllowed = 'move';
    };

    // Handle module drag over within a group
    const handleGroupModuleDragOver = (e, targetItemId, targetGroupId) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOverGroupModuleItemId(targetItemId);
        // Also set group drag over for visual feedback when dragging from different group or external
        if (draggedGroupModule && draggedGroupModule.groupId != targetGroupId) {
            setDragOverGroupId(targetGroupId);
        }
        // Detect external drag (from Modules page)
        const hasModuleData = e.dataTransfer.types.some(t => t.toLowerCase() === 'modulecode');
        if (!draggedGroupModule && !draggedPinnedItem && hasModuleData) {
            setIsExternalModuleDrag(true);
            setDragOverGroupId(targetGroupId);
        }
    };

    // Handle module drag end within a group
    const handleGroupModuleDragEnd = () => {
        setDraggedGroupModule(null);
        setDragOverGroupModuleItemId(null);
        setDragOverGroupId(null);
    };

    // Handle module drop within a group (reorder or move between groups)
    const handleGroupModuleDrop = async (e, targetGroupId, targetIndex, targetModule) => {
        e.preventDefault();
        e.stopPropagation();

        // Handle external drag from Modules page (via dataTransfer)
        if (!draggedGroupModule) {
            const moduleCode = e.dataTransfer.getData('moduleCode');
            const sourceGroupId = e.dataTransfer.getData('sourceGroupId') || e.dataTransfer.getData('groupId');

            if (moduleCode) {
                const numericModuleId = MODULE_ID_MAP[moduleCode];

                if (numericModuleId) {
                    // Don't do anything if dropping on same group
                    if (sourceGroupId && parseInt(sourceGroupId) == targetGroupId) {
                        handleGroupModuleDragEnd();
                        setIsExternalModuleDrag(false);
                        return;
                    }

                    try {
                        // If coming from another group, remove from source first
                        if (sourceGroupId) {
                            await removeModuleFromGroup(parseInt(sourceGroupId), numericModuleId);
                        }

                        // Add to target group
                        await addModuleToGroup(targetGroupId, numericModuleId);

                        // Reload groups data and notify other components
                        await loadGroups();
                        window.dispatchEvent(new CustomEvent('modulesGroupsChanged'));
                    } catch (error) {
                        console.error('Error moving module from Modules page:', error);
                    }
                }
            }

            handleGroupModuleDragEnd();
            setIsExternalModuleDrag(false);
            return;
        }

        const sourceGroupId = draggedGroupModule.groupId;
        const moduleCode = draggedGroupModule.module.code;

        // If dropping on a different group, move the module (use == for type coercion)
        if (sourceGroupId != targetGroupId) {
            const numericModuleId = MODULE_ID_MAP[moduleCode];
            if (numericModuleId) {
                try {
                    // Remove from source group
                    await removeModuleFromGroup(sourceGroupId, numericModuleId);
                    // Add to target group
                    await addModuleToGroup(targetGroupId, numericModuleId);
                    // Reload groups data and notify other components
                    await loadGroups();
                    window.dispatchEvent(new CustomEvent('modulesGroupsChanged'));
                } catch (error) {
                    console.error('Error moving module between groups:', error);
                }
            }
            handleGroupModuleDragEnd();
            return;
        }

        // Same group - reorder
        if (draggedGroupModule.module.itemid !== targetModule.itemid) {
            // Find the group in groupsData
            const groupIndex = groupsData.findIndex(g => g.id == targetGroupId);
            if (groupIndex === -1) {
                handleGroupModuleDragEnd();
                return;
            }

            const group = groupsData[groupIndex];
            const modules = [...(group.modules || [])];

            // Find actual indices in the full (unfiltered) modules array using itemid
            const draggedItemId = draggedGroupModule.module.itemid;
            const targetItemId = targetModule.itemid;

            const sourceIndex = modules.findIndex(m => m.itemid === draggedItemId);
            const destIndex = modules.findIndex(m => m.itemid === targetItemId);

            if (sourceIndex === -1 || destIndex === -1) {
                handleGroupModuleDragEnd();
                return;
            }

            // Reorder the modules
            const [removed] = modules.splice(sourceIndex, 1);
            modules.splice(destIndex, 0, removed);

            // Update local state immediately for responsive UI
            const newGroupsData = [...groupsData];
            newGroupsData[groupIndex] = { ...group, modules };
            setGroupsData(newGroupsData);
            localStorage.setItem('cabazaGroupsData', JSON.stringify(newGroupsData));

            // Get module IDs in new order for API call
            const moduleIds = modules.map(m => m.moduleid || m.moduleId || m.MODULEID);

            // Save to API
            try {
                await reorderGroupModules(targetGroupId, moduleIds);
            } catch (error) {
                console.error('Error saving module order:', error);
                // Reload groups to get correct order from server
                loadGroups();
            }
        }

        handleGroupModuleDragEnd();
    };

    // Fetch unread count periodically - DISABLED TEMPORARILY
    // useEffect(() => {
    //     const fetchUnread = async () => {
    //         const count = await getUnreadCount();
    //         setUnreadMessages(count);
    //     };
    //     fetchUnread();
    //     const interval = setInterval(fetchUnread, 30000); // Every 30 seconds
    //     return () => clearInterval(interval);
    // }, []);

    // Load profile photo
    useEffect(() => {
        const loadProfilePhoto = async () => {
            try {
                const userUuid = localStorage.getItem('userUuid');
                if (userUuid) {
                    const result = await getProfilePhoto(userUuid);
                    const success = result.success ?? result.SUCCESS;
                    const data = result.data ?? result.DATA;
                    if (success && data) {
                        const filePath = data.filePath ?? data.FILEPATH ?? data.filepath;
                        if (filePath) {
                            setProfilePhotoUrl(getFileUrl(filePath));
                        }
                    }
                }
            } catch (err) {
                console.error('Error loading profile photo:', err);
            }
        };
        loadProfilePhoto();
    }, []);

    // Check if any module has unread badge
    const hasModuleBadge = unreadMessages > 0;

    // Main menu items (top) - Dashboard, Search and Modules
    const mainMenuItems = [
        {
            id: 'dashboard',
            icon: Home,
            label: t.menu?.dashboard || 'Accueil',
        },
        {
            id: 'homedashboard',
            icon: BarChart3,
            label: t.menu?.homedashboard || 'Dashboard',
        },
        {
            id: 'search',
            icon: Search,
            label: t.menu?.search || 'Rechercher',
        },
        {
            id: 'modules',
            icon: LayoutGrid,
            label: t.menu?.modules || 'Modules',
            badge: hasModuleBadge ? unreadMessages : 0,
        },
    ];

    // Bottom menu items (Profil and Preferences)
    const bottomMenuItems = [
        {
            id: 'profile',
            icon: UserCircle,
            label: t.menu?.profile || 'Profile',
        },
        {
            id: 'preferences',
            icon: Settings,
            label: t.menu?.preferences || 'Settings',
        },
    ];

    // Check if theme has a background image
    const hasImageBg = currentTheme?.sidebarBgImage;
    const sidebarStyle = hasImageBg ? {
        backgroundImage: `url(${currentTheme.sidebarBgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
    } : {};

    // Check if module is enabled
    const isModuleEnabled = (moduleId) => {
        return enabledModules[moduleId] !== false;
    };

    // Get group data by id (only pinned and enabled modules)
    const getGroupData = (groupId) => {
        // Use == to handle number/string comparison
        const group = groupsData.find(g => g.id == groupId);
        if (!group) return null;
        return {
            ...group,
            modules: group.modules?.filter(m => {
                const navId = getNavId(m.code);
                // Only show modules that are enabled AND pinned (default to true if isPinned not set)
                const isPinnedValue = m.ispinned ?? m.isPinned ?? m.ISPINNED;
                const isPinned = isPinnedValue === undefined || isPinnedValue === null || isPinnedValue === true || isPinnedValue === 1 || isPinnedValue === '1';
                return isModuleEnabled(navId) && isPinned;
            }) || []
        };
    };

    // Get modules that are in ANY group (to exclude from individual display)
    const getModulesInGroups = () => {
        const modulesInGroups = new Set();
        // Check all groups - include ALL modules regardless of isPinned status
        // A module in a group should never show as individual, even if unpinned
        groupsData.forEach(group => {
            if (group.modules) {
                group.modules.forEach(m => {
                    const navId = getNavId(m.code);
                    modulesInGroups.add(navId);
                });
            }
        });
        return modulesInGroups;
    };

    // Get filtered pinned items (respecting enabled modules)
    const getFilteredPinnedItems = () => {
        const modulesInGroups = getModulesInGroups();
        return pinnedItems.filter(item => {
            if (item.type === 'module') {
                // Filter out disabled modules and modules in groups
                return isModuleEnabled(item.id) && !modulesInGroups.has(item.id);
            } else if (item.type === 'group') {
                // Filter out groups with no enabled modules
                const group = getGroupData(item.id);
                return group && group.modules && group.modules.length > 0;
            }
            return false;
        });
    };

    // Get module icon by code
    const getModuleIcon = (code) => {
        const icons = {
            'CONTACTS': Users,
            'CONVERSATIONS': MessageCircle,
            'MAP': MapPin,
            'AGENDA': Calendar,
            'EVENTS': PartyPopper,
            'PLACES': Building2,
            'SUPPORT': HeadphonesIcon,
            'WORK': Briefcase
        };
        return icons[code] || LayoutGrid;
    };

    // Get nav id from module code
    const getNavId = (code) => {
        const map = {
            'CONTACTS': 'contacts',
            'CONVERSATIONS': 'messages',
            'MAP': 'map',
            'AGENDA': 'calendar',
            'EVENTS': 'events',
            'PLACES': 'places',
            'SUPPORT': 'support',
            'WORK': 'work'
        };
        return map[code] || code.toLowerCase();
    };

    return (
        <div
            className="sidebar-menu w-20 h-screen flex flex-col items-center py-6 shadow-lg relative overflow-hidden"
            style={sidebarStyle}
        >
            {/* Background gradient or image overlay */}
            {hasImageBg ? (
                <div
                    className="absolute inset-0 z-0"
                    style={{ backgroundColor: currentTheme.sidebarBgOverlay || 'rgba(0,0,0,0.4)' }}
                />
            ) : (
                <div className="absolute inset-0 z-0 bg-gradient-to-b from-cabaza-purple to-cabaza-pink" />
            )}

            {/* Main Navigation Menu (top) */}
            <nav
                className={`flex-1 flex flex-col gap-4 w-full px-2 relative z-10 transition-all overflow-y-auto ${isDragOver ? 'bg-white/20 rounded-xl' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {mainMenuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = currentPage === item.id;

                    return (
                        <button
                            key={item.id}
                            onClick={() => onNavigate(item.id)}
                            className={`group relative flex flex-col items-center gap-1 p-3 rounded-xl transition-all duration-200 ${isActive
                                    ? 'bg-white/30 backdrop-blur-sm shadow-md'
                                    : 'hover:bg-white/10'
                                }`}
                            title={item.label}
                        >
                            <div className="relative">
                                <Icon
                                    className={`w-6 h-6 transition-colors ${isActive ? 'text-white' : 'text-white/70 group-hover:text-white'
                                        }`}
                                />
                                {item.badge > 0 && (
                                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                                        {item.badge > 9 ? '9+' : item.badge}
                                    </span>
                                )}
                            </div>
                            <span className={`text-[10px] font-medium transition-colors ${isActive ? 'text-white' : 'text-white/70 group-hover:text-white'
                                }`}>
                                {item.label.split(' ')[0]}
                            </span>
                        </button>
                    );
                })}

                {/* Unified Pinned Items (modules and groups) */}
                {(() => {
                    const filteredItems = getFilteredPinnedItems();
                    if (filteredItems.length === 0) return null;

                    return (
                    <div className="border-t border-white/20 pt-4 mt-2">
                        {filteredItems.map((item, index) => {
                            // Find actual index in pinnedItems (not filtered)
                            const actualIndex = pinnedItems.findIndex(p =>
                                p.type === item.type &&
                                (item.type === 'module' ? p.id === item.id : p.id === item.id)
                            );
                            const isDragging = draggedPinnedItem?.index === actualIndex;
                            const isLastItem = index === filteredItems.length - 1;
                            // Don't show drop indicator on the item being dragged, or on last item when dragging it
                            // Show drop indicator only when hovering a different item, and hide on last item only if dragging that same last item
                            const isDragOverItem = dragOverPinnedIndex === actualIndex && draggedPinnedItem?.index !== actualIndex && !(isLastItem && draggedPinnedItem?.index === actualIndex);

                            if (item.type === 'module') {
                                // Render module
                                const moduleDef = moduleDefinitions[item.id];
                                if (!moduleDef) return null;
                                const Icon = moduleDef.icon;
                                const isActive = currentPage === item.id;
                                const hasBadge = item.id === 'messages' && unreadMessages > 0;

                                return (
                                    <div
                                        key={`module-${item.id}`}
                                        draggable
                                        onDragStart={(e) => handlePinnedItemDragStart(e, item, actualIndex)}
                                        onDragOver={(e) => handlePinnedItemDragOver(e, actualIndex)}
                                        onDrop={(e) => handlePinnedItemDrop(e, actualIndex)}
                                        onDragEnd={handlePinnedItemDragEnd}
                                        className={`group relative flex flex-col items-center gap-1 p-3 rounded-xl transition-all duration-200 w-full mb-2 cursor-grab active:cursor-grabbing ${
                                            isActive
                                                ? 'bg-white/30 backdrop-blur-sm shadow-md'
                                                : 'hover:bg-white/10'
                                        } ${isDragging ? 'opacity-50' : ''} ${isDragOverItem ? 'border-2 border-white/50 border-dashed' : ''}`}
                                        title={moduleDef.label}
                                    >
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => onNavigate(item.id)}
                                            onKeyDown={(e) => e.key === 'Enter' && onNavigate(item.id)}
                                            className="flex flex-col items-center gap-1 w-full cursor-pointer"
                                        >
                                            <div className="relative">
                                                <Icon
                                                    className={`w-6 h-6 transition-colors ${isActive ? 'text-white' : 'text-white/70 group-hover:text-white'}`}
                                                />
                                                {hasBadge && (
                                                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                                                        {unreadMessages > 9 ? '9+' : unreadMessages}
                                                    </span>
                                                )}
                                            </div>
                                            <span className={`text-[10px] font-medium transition-colors text-center leading-tight ${isActive ? 'text-white' : 'text-white/70 group-hover:text-white'}`}>
                                                {moduleDef.label}
                                            </span>
                                        </div>
                                    </div>
                                );
                            } else if (item.type === 'group') {
                                // Render group - always show as folder icon, click opens popup
                                const group = getGroupData(item.id);
                                if (!group) return null;
                                const isGroupDragOver = dragOverGroupId === group.id;
                                const isPopupOpen = openGroupPopup === group.id;

                                return (
                                    <div
                                        key={`group-${group.id}`}
                                        className={`mb-2 ${isDragging ? 'opacity-50' : ''} ${isDragOverItem ? 'border-2 border-white/50 border-dashed rounded-xl' : ''}`}
                                        draggable
                                        onDragStart={(e) => handlePinnedItemDragStart(e, item, actualIndex)}
                                        onDragOver={(e) => {
                                            if (draggedPinnedItem) {
                                                handlePinnedItemDragOver(e, actualIndex);
                                            } else {
                                                handleGroupDragOver(e, group.id);
                                            }
                                        }}
                                        onDragLeave={handleGroupDragLeave}
                                        onDrop={(e) => {
                                            if (draggedGroupModule) {
                                                handleDropOnGroup(e, group.id);
                                            } else if (draggedPinnedItem) {
                                                handlePinnedItemDrop(e, actualIndex);
                                            } else {
                                                handleDropOnGroup(e, group.id);
                                            }
                                        }}
                                        onDragEnd={handlePinnedItemDragEnd}
                                    >
                                        {/* Group folder icon - click to open popup */}
                                        <div
                                            onClick={() => setOpenGroupPopup(isPopupOpen ? null : group.id)}
                                            className={`group relative flex flex-col items-center gap-1 p-3 rounded-xl transition-all duration-200 w-full hover:bg-white/10 cursor-pointer ${isPopupOpen ? 'bg-white/30' : ''} ${isGroupDragOver && (draggedGroupModule || isExternalModuleDrag || !draggedPinnedItem) ? 'bg-white/20 border-2 border-white/50 border-dashed' : ''}`}
                                            title={group.name}
                                        >
                                            <div
                                                className="w-6 h-6 rounded-lg flex items-center justify-center"
                                                style={{ backgroundColor: group.color || '#8b5cf6' }}
                                            >
                                                <Folder className="w-4 h-4 text-white" />
                                            </div>
                                            <span className={`text-[10px] font-medium text-center leading-tight max-w-full ${isPopupOpen ? 'text-white' : 'text-white/70 group-hover:text-white'}`}>
                                                {group.name || ''}
                                            </span>
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })}
                    </div>
                    );
                })()}

                {/* Drop zone hint - only show when dragging external items, not when reordering or moving between groups */}
                {isDragOver && !draggedPinnedItem && !draggedGroupModule && (
                    <div className="border-2 border-dashed border-white/50 rounded-xl p-4 flex items-center justify-center">
                        <span className="text-white/70 text-xs text-center">Déposer ici</span>
                    </div>
                )}
            </nav>

            {/* Bottom Menu (Profil, Abonnement, Sortir) */}
            <div className="flex flex-col gap-2 w-full px-2 relative z-10">
                {bottomMenuItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = currentPage === item.id;
                    const isProfileItem = item.id === 'profile';

                    return (
                        <button
                            key={item.id}
                            onClick={() => onNavigate(item.id)}
                            className={`group relative flex flex-col items-center gap-1 p-3 rounded-xl transition-all duration-200 ${isActive
                                    ? 'bg-white/30 backdrop-blur-sm shadow-md'
                                    : 'hover:bg-white/10'
                                }`}
                            title={item.label}
                        >
                            {isProfileItem && profilePhotoUrl ? (
                                <img
                                    src={profilePhotoUrl}
                                    alt="Profile"
                                    className={`w-6 h-6 rounded-full object-cover border-2 transition-colors ${isActive ? 'border-white' : 'border-white/70 group-hover:border-white'}`}
                                />
                            ) : (
                                <Icon
                                    className={`w-6 h-6 transition-colors ${isActive ? 'text-white' : 'text-white/70 group-hover:text-white'
                                        }`}
                                />
                            )}
                            <span className={`text-[10px] font-medium transition-colors ${isActive ? 'text-white' : 'text-white/70 group-hover:text-white'
                                }`}>
                                {isProfileItem ? ((localStorage.getItem('userName') || '').split(' ')[0] || item.label.split(' ')[0]) : item.label.split(' ')[0]}
                            </span>
                        </button>
                    );
                })}

                {/* Logout button */}
                <button
                    onClick={onLogout}
                    className="group flex flex-col items-center gap-1 p-3 rounded-xl hover:bg-white/10 transition-all duration-200"
                    title={t.menu?.logoutTitle || 'Logout'}
                >
                    <LogOut className="w-6 h-6 text-white/70 group-hover:text-white transition-colors" />
                    <span className="text-[10px] text-white/70 group-hover:text-white font-medium transition-colors">
                        {t.menu?.logout || 'Logout'}
                    </span>
                </button>
            </div>

            {/* Group Popup Overlay - Portal to render outside sidebar */}
            {openGroupPopup && createPortal(
                <GroupPopupOverlay
                    group={getGroupData(openGroupPopup)}
                    onClose={() => setOpenGroupPopup(null)}
                    onNavigate={(navId) => {
                        onNavigate(navId);
                        setOpenGroupPopup(null);
                    }}
                    getModuleIcon={getModuleIcon}
                    getNavId={getNavId}
                    currentPage={currentPage}
                    unreadMessages={unreadMessages}
                />,
                document.body
            )}
        </div>
    );
};

/**
 * Group Popup Overlay Component
 * Displays modules in a 3x4 grid overlay
 */
const GroupPopupOverlay = ({ group, onClose, onNavigate, getModuleIcon, getNavId, currentPage, unreadMessages }) => {
    const popupRef = useRef(null);

    // Module colors (same as PageHeader)
    const MODULE_COLORS = {
        CONTACTS: '#3b82f6',      // blue
        CONVERSATIONS: '#8b5cf6', // purple
        MAP: '#22c55e',           // green
        AGENDA: '#f97316',        // orange
        EVENTS: '#ec4899',        // pink
        PLACES: '#8b5cf6',        // purple
    };

    // Close on click outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (popupRef.current && !popupRef.current.contains(e.target)) {
                // Also check if click is not on the sidebar group button
                const sidebarGroup = document.querySelector(`[title="${group?.name}"]`);
                if (!sidebarGroup?.contains(e.target)) {
                    onClose();
                }
            }
        };

        // Close on Escape key
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose, group?.name]);

    if (!group || !group.modules || group.modules.length === 0) return null;

    // Limit to 12 modules (3x4 grid)
    const displayModules = group.modules.slice(0, 12);

    return (
        <>
            {/* Semi-transparent backdrop - covers the page content */}
            <div
                className="fixed inset-0 left-20 bg-black/30 z-[9997] pointer-events-auto"
                onClick={onClose}
            />

            {/* Popup container - positioned to the right of sidebar, above backdrop */}
            <div
                ref={popupRef}
                className="fixed left-24 top-1/2 -translate-y-1/2 z-[9999] pointer-events-auto"
            >
                <div
                    className="bg-white rounded-2xl shadow-2xl p-4 min-w-[280px] relative overflow-hidden"
                >
                    {/* Left border gradient (same as sidebar) */}
                    <div
                        className="absolute left-0 top-0 bottom-0 w-1 rounded-l-2xl bg-gradient-to-b from-cabaza-purple to-cabaza-pink"
                    />
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <div
                                className="w-8 h-8 rounded-lg flex items-center justify-center"
                                style={{ backgroundColor: group.color || '#8b5cf6' }}
                            >
                                <Folder className="w-5 h-5 text-white" />
                            </div>
                            <span className="font-semibold text-gray-800">{group.name}</span>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>

                    {/* Modules Grid - 2 columns */}
                    <div className="grid grid-cols-2 gap-3">
                        {displayModules.map((module) => {
                            const Icon = getModuleIcon(module.code);
                            const navId = getNavId(module.code);
                            const isActive = currentPage === navId;
                            const hasBadge = navId === 'messages' && unreadMessages > 0;
                            // Use MODULE_COLORS mapping first (consistent colors), fallback to DB color
                            const moduleColor = MODULE_COLORS[module.code] || MODULE_COLORS[module.code?.toUpperCase()] || module.color || '#8b5cf6';

                            return (
                                <button
                                    key={module.itemid || module.code}
                                    onClick={() => onNavigate(navId)}
                                    className={`group flex flex-col items-center gap-2 p-3 rounded-xl transition-all duration-200 ${
                                        isActive
                                            ? 'bg-gradient-to-br from-cabaza-purple to-cabaza-pink text-white shadow-md'
                                            : 'hover:bg-gray-100 text-gray-700'
                                    }`}
                                >
                                    <div className="relative">
                                        <Icon
                                            className="w-7 h-7 transition-colors"
                                            style={{ color: isActive ? 'white' : moduleColor }}
                                        />
                                        {hasBadge && (
                                            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                                                {unreadMessages > 9 ? '9+' : unreadMessages}
                                            </span>
                                        )}
                                    </div>
                                    <span className={`text-xs font-medium text-center leading-tight ${isActive ? 'text-white' : 'text-gray-700'}`}>
                                        {module.label || module.name || navId}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </>
    );
};

export default Menu;
